import * as EmailValidator from "email-validator";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { UserApi } from "../../api/UserApi";
import { IPlanInfo } from "../../model/ClientsAndPlans.model";
import { INewUser } from "../../model/NewUser.model";
import { getUserTypes, UserTypeEnum } from "../../model/UserInfo.model";
import { prominentButtonStyle, secondaryButtonStyle } from "../common/buttonStyles";
import { RaisedButton } from "../common/RaisedButton";
import { byName } from "../../utils/listUtil";
import { setHeaderNotification } from "../base/header/HeaderActions";
import { NotificationTypes } from "../base/header/HeaderReducer";
import { ISelectValue, SelectComponent } from "../common/Select.component";
import { TextFieldComponent } from "../common/TextField.component";
import { LoadingSpinner } from "../icons/LoadingSpinner.component";
import AdminAddPlanComponent from "./AdminAddPlan.component";
import { ApiError } from "../../model/ApiError.model";
import { MFA_SELECT_VALUES } from "./AdminUserEdit.component";
import { scrollToElement } from "../../utils/browserUtil";
import {useNavigate} from "react-router-dom";
import {navigateTo} from "../../../navigateTo";

export interface IAdminUserAddComponentState {
    firstName: string;
    lastName: string;
    email: string;
    userType: string | undefined;
    plans: IPlanInfo[];
    saveInProgress: boolean;
    submitClicked: boolean;
    isMfa: boolean;
}

export interface IAdminUserAddProps {
    actions: {
        setHeaderNotification: typeof setHeaderNotification;
    };
}

const initialAddUserState: IAdminUserAddComponentState = {
    firstName: "",
    lastName: "",
    email: "",
    userType: undefined,
    plans: [],
    saveInProgress: false,
    submitClicked: false,
    isMfa: true
};

export const AdminUserAddComponent: React.FC<IAdminUserAddProps> = (props) => {
    const [state, setState] = useState<IAdminUserAddComponentState>(initialAddUserState);

    const navigate = useNavigate();

    const handleSelectPlan = (arg: IPlanInfo | undefined) => {
        const planInfo = arg as IPlanInfo;
        if (state.plans.find((plan) => plan.id === planInfo.id)) {
            return;
        }
        const newPlanArray = [...state.plans, planInfo].sort(byName);
        setState((prev) => ({ ...prev, plans: newPlanArray }));
    };

    const handleFailure = (reason: string | undefined) => {
        setState((prev) => ({ ...prev, saveInProgress: false }));
        props.actions.setHeaderNotification({
            message: `${reason}`,
            notificationType: NotificationTypes.FAILURE,
        }, 5000);
    };

    const handleSave = () => {
        if (
            state.firstName.length === 0 ||
            state.lastName.length === 0 ||
            state.email.length === 0 ||
            state.plans.length === 0 ||
            !state.userType
        ) {
            setState((prev) => ({ ...prev, submitClicked: true }));
            props.actions.setHeaderNotification({
                message: "Please complete all required fields before saving",
                notificationType: NotificationTypes.FAILURE,
            }, 5000);
            return;
        }

        if (!EmailValidator.validate(state.email)) {
            props.actions.setHeaderNotification({
                message: "invalid email format",
                notificationType: NotificationTypes.FAILURE,
            }, 5000);
            return;
        }

        const plans = state.plans.map((plan) => plan.id);

        const user: INewUser = {
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            userType: state.userType.toUpperCase() as UserTypeEnum,
            isMfa: state.isMfa,
            planIds: plans,
        };

        setState((prev) => ({ ...prev, saveInProgress: true }));

        UserApi.addUser(user)
            .then(
                () => {
                    props.actions.setHeaderNotification({
                        message: `${user.firstName} ${user.lastName} Successfully saved`,
                        notificationType: NotificationTypes.SUCCESS,
                    }, 5000);
                    resetState();
                },
                (rejected: { message: string }) => {
                    handleFailure(rejected.message);
                }
            )
            .catch((reason: ApiError) => {
                handleFailure(reason.message);
            });
        scrollToElement(".main__header", 0);
    };

    const handleCancel = () => {
        navigateTo("/admin", navigate);
    };

    const resetState = () => {
        setState(initialAddUserState);
    };

    const handleRemovePlan = (planInfo: IPlanInfo) => {
        const newPlansList = state.plans.filter((plan) => plan.id !== planInfo.id);
        setState((prev) => ({ ...prev, plans: newPlansList }));
    };

    const renderUserPlansSection = () => {
        const renderUserPlan = (x: IPlanInfo) => (
            <div className="admin-user-add-page__user-plan"
                 key={x.id}
                 data-testid="admin-user-add-page__user-plan">
                <div className="remove-icon fas fa-x" onClick={() => handleRemovePlan(x)} />
                <div>{x.name}</div>
                <br />
            </div>
        );

        return (
            <div className="admin-user-add-page__user-plan-section"
                 data-testid="admin-user-add-page__user-plan-section">
                <h2>Current Plans</h2>
                {state.plans.map(renderUserPlan)}
            </div>
        );
    };

    const renderFirstNameField = () => (
        <TextFieldComponent
            name="first_name"
            className="admin-user-add-page__first-name-field"
            type="text"
            fullWidth={true}
            value={state.firstName}
            handleChange={(e: any) => { setState((prev) => ({ ...prev, firstName: e.target.value })); }}
            placeholder="First Name"
            renderRequired={true}
            submitClicked={state.submitClicked}
        />
    );

    const renderLastNameField = () => (
        <TextFieldComponent
            name="last_name"
            className="admin-user-add-page__last-name-field"
            type="text"
            fullWidth={true}
            value={state.lastName}
            handleChange={(e: any) => { setState((prev) => ({ ...prev, lastName: e.target.value })); }}
            placeholder="Last Name"
            renderRequired={true}
            submitClicked={state.submitClicked}
        />
    );

    const renderEmailField = () => (
        <TextFieldComponent
            name="email"
            className="admin-user-add-page__email-field"
            type="text"
            fullWidth={true}
            value={state.email}
            handleChange={(e: any) => { setState((prev) => ({ ...prev, email: e.target.value })); }}
            placeholder="Email"
            renderRequired={true}
            submitClicked={state.submitClicked}
        />
    );

    const renderUserTypeDropDown = () => {
        const userTypes: ISelectValue[] = getUserTypes();

        return (
            <div className="admin-user-add-page__user-type-mfa">
                <SelectComponent
                    id={"admin-user-add-page__user-type"}
                    values={userTypes}
                    selected={state.userType}
                    width={230}
                    handleChange={(e: any) => {
                        setState((prev) => ({ ...prev, userType: e.target.value }));
                    }}
                    placeholder={"User Type"}
                    renderRequired={true}
                    submitClicked={state.submitClicked}
                />
            </div>
        );
    };

    const renderMfaGroupDropDown = () => (
        <div className="admin-user-add-page__user-type-mfa">
            <SelectComponent
                id={"admin-user-add-page__user-mfa"}
                values={MFA_SELECT_VALUES}
                selected={Number(state.isMfa)}
                width={150}
                submitClicked={false}
                handleChange={(e: any) => {
                    setState((prev) => ({ ...prev, isMfa: Boolean(e.target.value) }));
                }}
                placeholder={"Mfa Type"}
                renderRequired={false}
            />
        </div>
    );

    const renderButtons = () => (
        <div>
            <div className="buttons-spacer-top" />
            <div className="save-and-cancel-buttons">
                <div className="buttons-spacer-left" />
                <RaisedButton className="admin-user-add-page__cancel-button"
                              style={secondaryButtonStyle}
                              primary={false}
                              onClick={handleCancel}>
                    Cancel
                </RaisedButton>
                <div className="buttons-spacer-between" />
                <RaisedButton className="admin-user-add-page__save-button"
                              style={prominentButtonStyle}
                              primary={true}
                              onClick={handleSave}>
                    Save
                </RaisedButton>
            </div>
            <div className="buttons-spacer-bottom" />
        </div>
    );

    if (state.saveInProgress) {
        return <LoadingSpinner />;
    }

    return (
        <div className="admin-user-add-page" data-testid="admin-user-add-page">
            <h1>Add User</h1>
            <div className="admin-user-add-page__edit-field">
                {renderFirstNameField()}
                {renderLastNameField()}
                {renderEmailField()}
                {renderUserTypeDropDown()}
                {renderMfaGroupDropDown()}
            </div>
            {renderUserPlansSection()}
            <h2>Add Plan(s)</h2>
            <AdminAddPlanComponent
                onAddPlan={handleSelectPlan}
                renderRequired={true}
                submitClicked={state.submitClicked}
                plansSelected={state.plans.length > 0}
            />
            {renderButtons()}
        </div>
    );
};

export function mapDispatchToProps(dispatch: Dispatch<AnyAction>): IAdminUserAddProps {
    return {
        actions: bindActionCreators({
            setHeaderNotification,
        }, dispatch),
    };
}

export default connect<{}, IAdminUserAddProps>(null, mapDispatchToProps)(AdminUserAddComponent);